import React, { useState, useEffect, Fragment } from "react";
import { useDropzone } from "react-dropzone";
import moment from "moment";
import { bool, func, any, number } from "prop-types";
import * as style from "./styles.module.scss";
import { Overlay, PulseLoader, Button } from "../../common";
import { CvFileIcon } from "../EditCv";
import { openCv } from "../../helpers/helpers";
import { cvStatuses } from "../../helpers/cv";

const CvPopupInternals = ({
  cvStatus,
  getRootProps,
  getInputProps,
  open,
  pickedFile,
  handleSendCv,
  uploadProgress,
  handleCloseClick,
}) => {
  const filename = (pickedFile && pickedFile.path) || "";
  const extension =
    (pickedFile && pickedFile.path && pickedFile.path.split(".")[1]) || "";
  switch (cvStatus) {
    case cvStatuses.Empty:
      return (
        <div
          {...getRootProps({ className: "dropzone" })}
          className={style.cvpopup}
        >
          <input {...getInputProps()} />
          <p className={style.cvpopup__dropzone_title}>
            Drop your CV file here
          </p>
          <span className={style.cvpopup__dropzone_subtitle}>
            Your CV needs to be provided as doc, docx or PDF files.
          </span>
          <div className="d-flex align-items-center my-1">
            <span className={style.cvpopup__dropzone_line} />
            <span className="px-1 font-weight-bold">or</span>
            <span className={style.cvpopup__dropzone_line} />
          </div>
          <button
            className={`btn btn-outline-info text-dark bg-white ${style.cvpopup__button}`}
            onClick={open}
          >
            Choose a file
          </button>
        </div>
      );
    case cvStatuses.FileSelected:
      return (
        <div
          {...getRootProps({ className: "dropzone" })}
          className={`${style.cvpopup} ${style.cvpopup__picked}`}
        >
          <input {...getInputProps()} />
          <CvFileIcon isBig text={extension} />
          <h4>{filename}</h4>
          <button
            className={`btn btn-outline-info text-dark bg-white ${style.cvpopup__button}`}
            onClick={open}
          >
            Choose a different file
          </button>
        </div>
      );
    case cvStatuses.WrongFile:
      return (
        <div
          {...getRootProps({ className: "dropzone" })}
          className={style.cvpopup}
        >
          <input {...getInputProps()} />
          <CvFileIcon isBig text={"!"} />
          <p className={style.cvpopup__dropzone_title}>Wrong file type</p>
          <span className={style.cvpopup__dropzone_subtitle}>
            Your CV needs to be provided as doc, docx or PDF files.
          </span>
          <span className={style.cvpopup__dropzone_subtitle}>
            Drop another file here to try again.
          </span>
          <div className="d-flex align-items-center my-1">
            <span className={style.cvpopup__dropzone_line} />
            <span className="px-1 font-weight-bold">or</span>
            <span className={style.cvpopup__dropzone_line} />
          </div>
          <button
            className={`btn btn-outline-info text-dark bg-white ${style.cvpopup__button}`}
            onClick={open}
          >
            Choose a different file
          </button>
        </div>
      );
    case cvStatuses.Loading:
      return (
        <div
          {...getRootProps({ className: "dropzone" })}
          className={`${style.cvpopup} ${style.cvpopup__picked}`}
        >
          <input {...getInputProps()} />
          <CvFileIcon isBig text={extension} />
          <h4>{filename}</h4>
          <progress
            className={style.cvpopup__progress}
            value={uploadProgress}
            max={100}
          />
        </div>
      );
    case cvStatuses.Error:
      return (
        <div className="w-100">
          <div className="w-100 h-100 d-flex justify-content-center align-items-center my-4">
            <div className="w-100 h-100 d-flex flex-column justify-content-center align-items-center">
              <img src="/images/icons/timesheet-error.svg" />
              <h4 className={`mt-2 text-center`}>Upload failed</h4>
              <span className="text-center font-weight-bold">
                There has been an error uploading your CV.
              </span>
              <span className="text-center font-weight-bold">
                Please click the button below to retry.
              </span>
            </div>
          </div>
          <div className="w-100 mr-1 d-flex justify-content-end">
            <button
              className="btn btn-primary font-weight-bold"
              onClick={handleSendCv}
            >
              Try Again
            </button>
          </div>
        </div>
      );
    case cvStatuses.Success:
      return (
        <div className="w-100">
          <div className="w-100 d-flex justify-content-center align-items-center my-4">
            <div className="w-100 h-100 d-flex flex-column justify-content-center align-items-center">
              <img src="/images/icons/timesheet-approve.svg" />
              <h4 className={`mt-2 text-center`}>CV uploaded successfully</h4>
              <span className="text-center mx-2 font-weight-bold">
                Your CV has been successfully uploaded. You can easily update
                this file in the future if your information changes.
              </span>
            </div>
          </div>
          <div className="w-100 mr-1 d-flex justify-content-center">
            <button
              className={`btn btn-primary ${style.cvpopup__cancel} mb-2`}
              onClick={handleCloseClick}
              disabled={cvStatus === cvStatuses.Loading}
            >
              Back to my account
            </button>
          </div>
        </div>
      );
    default:
      return <div>Something went wrong.</div>;
  }
};

CvPopupInternals.propTypes = {
  cvStatus: number,
  getRootProps: func,
  getInputProps: func,
  open: func,
  pickedFile: any,
  handleSendCv: func,
  uploadProgress: number,
  handleCloseClick: func,
};

const calculateCvStatus = (
  isFileAccepted,
  isFileRejected,
  cvStatus,
  cvErrors,
  isCvLoading
) => {
  if (isFileAccepted && cvStatus !== cvStatuses.Loading && !cvErrors)
    return cvStatuses.FileSelected;
  if (isFileRejected && cvStatus !== cvStatuses.Loading && !cvErrors)
    return cvStatuses.WrongFile;
  if (cvStatus === cvStatuses.Loading && cvErrors && !isCvLoading)
    return cvStatuses.Error;
  if (cvStatus === cvStatuses.Loading && !cvErrors && !isCvLoading)
    return cvStatuses.Success;
  return cvStatus;
};

const CvPopup = ({
  isOpen,
  setOpen,
  handleSubmit,
  errors,
  clearErrorsWithSource,
  isCvLoading,
  appData,
}) => {
  const [cvStatus, setCvStatus] = useState(cvStatuses.Empty);
  const uploadProgress = appData.uploadProgress || 0;
  const { getRootProps, getInputProps, open, acceptedFiles, fileRejections } =
    useDropzone({
      multiple: false,
      noClick: true,
      noKeyboard: true,
      accept: ".doc,.docx,.pdf",
    });
  const hasExistingCv = appData.cv && appData.cv.fileKey;
  const existingCvName = appData.cv && appData.cv.fileName;
  const existingCvDate =
    existingCvName &&
    existingCvName.split("_")[1] &&
    moment(existingCvName.split("_")[1]);
  const cvErrors =
    errors && errors.filter((error) => error.source === "cv").length > 0;
  const isFileRejected = fileRejections && fileRejections.length > 0;
  const isFileAccepted = acceptedFiles && acceptedFiles.length > 0;
  const pickedFile = isFileAccepted && acceptedFiles[0];
  const handleSendCv = () => {
    setCvStatus(cvStatuses.Loading);
    clearErrorsWithSource("cv");
    handleSubmit(pickedFile);
  };
  const [hasPastCv, setHasPastCv] = useState(hasExistingCv || isCvLoading);
  useEffect(() => {
    setCvStatus(
      calculateCvStatus(
        isFileAccepted,
        isFileRejected,
        cvStatus,
        cvErrors,
        isCvLoading
      )
    );
  }, [acceptedFiles, fileRejections, errors, isCvLoading]);
  const handleCloseClick = () => {
    if (cvStatus !== cvStatuses.Loading) {
      setCvStatus(cvStatuses.Empty);
      setHasPastCv(hasExistingCv || isCvLoading);
      setOpen(false);
    }
  };
  return (
    <div>
      <Overlay show={isOpen} onOutsideClick={handleCloseClick} overNav>
        <div className={`p-1 bg-white rounded ${style.cvpopup__wrapper}`}>
          <Fragment>
            <h4>{hasPastCv ? "Update your CV" : "Upload your CV"}</h4>
            {hasPastCv && (
              <div className={`text-center`}>
                <p className="mb-0">
                  Review your current CV or upload a new one.
                </p>
                {existingCvDate && (
                  <div
                    className={`text-center d-flex flex-column align-items-center`}
                  >
                    Your current CV was added on the
                    <h3 className={`mb-0 mt-1`}>
                      {existingCvDate.format("DD/MM/YYYY")}
                    </h3>
                  </div>
                )}
                <div className="mx-2">
                  <Button
                    onClick={() => openCv(appData.cv)}
                    disabled={isCvLoading}
                    classes="btn btn-outline-dark btn-block m-2 mx-auto font-weight-bold d-flex justify-content-center"
                    styles={{ width: 264 }}
                  >
                    {isCvLoading ? <PulseLoader isSmall /> : "Download a copy"}
                  </Button>
                </div>
              </div>
            )}
            <CvPopupInternals
              cvStatus={cvStatus}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
              open={open}
              pickedFile={pickedFile}
              handleSendCv={handleSendCv}
              uploadProgress={uploadProgress}
              handleCloseClick={handleCloseClick}
            />
            {cvStatus !== cvStatuses.Success && (
              <div className="d-flex justify-content-end w-100 mt-1">
                <button
                  className={`btn btn-outline-dark text-dark ${style.cvpopup__cancel}`}
                  onClick={handleCloseClick}
                  disabled={cvStatus === cvStatuses.Loading}
                >
                  Back to My Account
                </button>
                <button
                  className="btn btn-primary ml-1"
                  onClick={handleSendCv}
                  disabled={
                    cvStatus === cvStatuses.Empty ||
                    cvStatus === cvStatuses.WrongFile ||
                    cvStatus === cvStatuses.Loading
                  }
                >
                  Upload
                </button>
              </div>
            )}
          </Fragment>
        </div>
      </Overlay>
    </div>
  );
};

CvPopup.propTypes = {
  isOpen: bool,
  setOpen: func,
  handleSubmit: func,
  errors: any,
  clearErrorsWithSource: func,
  isCvLoading: bool,
  appData: any,
};

export default CvPopup;
